import PropTypes from 'prop-types';
import React from 'react';
import { useController, useForm } from 'react-hook-form';

import ModalHeader from '@components/modals/ModalHeader/ModalHeader';
import Button from '@components/uikit/Button/Button';
import Input from '@components/uikit/Input/Input';
import TreeSelect from '@components/uikit/TreeSelect/TreeSelect';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useLocationBlockStoreAction } from '@pages/ManageLocations/store/useLocationBlockStore';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useLocationsSaveMutation } from '@services/useLocationsSaveMutation';

import { useModalActions } from '@store/useModalStore';

import {
  CREATE_LOCATION_FIELDS,
  createLocationDefaultValue,
  schemaCreateLocation,
} from './constants/schemaCreateLocation';
import style from './style.module.scss';

const propTypes = {
  onClose: PropTypes.func,
};

const CreateLocationModal = ({ onClose }) => {
  const { t } = useTranslation();

  const { data: assetTreeData } = useAssetTreeQuery();

  const { setModalClose } = useModalActions();
  const { setParentLocationId } = useLocationBlockStoreAction();

  const { mutate: locationsSaveMutate, isLoading: locationsSaveIsLoading } = useLocationsSaveMutation();

  const { control, handleSubmit, reset, setError } = useForm({
    mode: 'all',
    resolver: schemaCreateLocation,
    defaultValues: createLocationDefaultValue,
  });

  const handleSave = handleSubmit((formData) => {
    setParentLocationId(formData.parent_id);
    locationsSaveMutate(
      {
        name: formData.name,
        parentId: formData.parent_id,
      },
      {
        onSuccess: (result) => {
          if (result?.data?.status === 'error') {
            setError('name', { message: result?.data?.errors.name });

            return;
          }
          showToast({ variant: 'success', message: t('The location was successfully created.') });
          handleClose();
        },
        onError: (err) => {
          if (err?.response?.status === 422) {
            setError('name', { message: err?.response?.data?.errors?.name });
          }
        },
      },
    );
  });

  const handleClose = () => {
    setModalClose();
    reset();

    if (onClose) {
      onClose();
    }
  };

  return (
    <div className={style.modalCreateLocation}>
      <ModalHeader onClose={handleClose} />
      <div className={style.modalBody}>
        <div className={style.modalBodyTitle}>{t('Create Location')}</div>
        <div className={style.modalBodyForm}>
          <Input
            name={CREATE_LOCATION_FIELDS.NAME}
            control={control}
            label={t('Location Name')}
            placeholder={t('Enter New Location Name')}
            required
          />
          <TreeSelect
            label={t('Parent')}
            required
            items={assetTreeData?.facilities}
            name={CREATE_LOCATION_FIELDS.PARENT_ID}
            searchable
            control={control}
            fieldNames={{
              children: 'children',
              title: 'name',
              key: 'id',
            }}
            placeholder={t('Start To Type...')}
          />
        </div>
      </div>
      <div className={style.modalFooter}>
        <Button type="primary" text={t('Save')} disabled={locationsSaveIsLoading} onClick={handleSave} />
        <Button type="secondary" text={t('Cancel')} disabled={locationsSaveIsLoading} onClick={handleClose} />
      </div>
    </div>
  );
};

CreateLocationModal.propTypes = propTypes;

export default CreateLocationModal;
